import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'

import { FieldError } from '@/components/feedback/FieldError'
import { useToastStack } from '@/components/feedback/toast-stack'
import { DateInput } from '@/components/filters/DateInput'
import { MonthPicker } from '@/components/filters/MonthPicker'
import { OptionSelect } from '@/components/filters/OptionSelect'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useInvalidateCash } from '@/features/cash/hooks'
import {
  emptyManualItemValues,
  manualItemBody,
  manualItemSchema,
  manualItemValues,
  type ManualItemFormValues,
} from '@/features/cash/manual-item-form'
import { INSTRUMENTS } from '@/features/cash/manual-item-grid'
import { previewPaymentDate } from '@/lib/cash'
import { formatDateOnly, formatDecimal } from '@/lib/format'
import { fieldErrorProps } from '@/lib/form/field-error'
import { useRecordEditor } from '@/lib/form/use-record-editor'
import { useRecordVersion } from '@/lib/form/use-record-version'
import { messages } from '@/lib/messages'
import { toProblem } from '@/lib/problem'
import { queryKeys } from '@/lib/query-keys'
import { cashApi, type CashManualItem, type CashSettings } from '@/services/http/cash'

const t = messages.cash.manual

/** ERP dışı kalemin ayrıntı editörü (öneri, sonraki aylara kopyalama); ızgaradaki kalem kaleminden açılır. */
export function ManualItemSheet({
  firm,
  settings,
  start,
  item,
  open,
  onClose,
  onReloaded,
}: {
  firm: string
  settings: CashSettings
  start: string
  item: CashManualItem | null
  open: boolean
  onClose: () => void
  /** "Yeniden yükle" sonrası kalemin güncel hâli (`null`: kalem artık yok); sayfa taslağı ondan kurar. */
  onReloaded: (fresh: CashManualItem | null) => void
}) {
  const form = useForm<ManualItemFormValues>({
    resolver: zodResolver(manualItemSchema),
    defaultValues: item
      ? manualItemValues(item)
      : emptyManualItemValues(settings.types[0]?.name ?? ''),
  })
  const errors = form.formState.errors
  const [copyMonths, setCopyMonths] = useState('3')
  const [copyIncrease, setCopyIncrease] = useState('0')
  const { toast } = useToastStack()
  const invalidate = useInvalidateCash()
  const queryClient = useQueryClient()
  // Taslak açılışta `item`dan kopyalanır (`item` listeden tıklama anındaki anlık görüntüdür); sürüm o
  // anki kalemin sürümüne sabit kalır, arka plan tazelemesi onu ilerletmez. Yeni kalemde sürüm yok.
  const version = useRecordVersion({
    recordId: item?.id ?? null,
    loaded: item?.version ?? null,
    dirty: true,
    reload: async () => {
      const list = await queryClient.fetchQuery({
        queryKey: queryKeys.cash.manual(firm),
        queryFn: () => cashApi.manualItems(firm),
        staleTime: 0,
      })
      onReloaded(list.find((m) => m.id === item?.id) ?? null)
    },
  })
  const onError = (error: unknown) =>
    toast({
      type: 'error',
      title: messages.cash.planning.failed,
      description: toProblem(error).detail ?? undefined,
    })

  const editor = useRecordEditor({
    form,
    // Güncelleme okunan sürümü gönderir; yeni kalemde sürüm yoktur.
    submit: (values) =>
      item
        ? cashApi.updateManual(item.id, manualItemBody(firm, values, version.current()))
        : cashApi.createManual(manualItemBody(firm, values)),
    onSaved: (saved) => {
      version.advance(saved.version)
      // Tazeleme gelene kadar hızlı yeniden açılış yeni sürümle başlasın (sahte 409 olmasın).
      queryClient.setQueryData<CashManualItem[]>(queryKeys.cash.manual(firm), (old) =>
        old?.map((m) => (m.id === saved.id ? saved : m)),
      )
      toast({ type: 'success', title: t.saved })
      void invalidate()
      onClose()
    },
    version,
    failedTitle: messages.cash.planning.failed,
  })
  const copy = useMutation({
    mutationFn: () =>
      cashApi.copyManual(firm, item!.id, Number(copyMonths), Number(copyIncrease) || 0),
    onSuccess: (rows) => {
      toast({ type: 'success', title: t.copied(rows.length) })
      void invalidate()
      onClose()
    },
    onError,
  })

  // Tüm form izlenir (varsayılanlarla başladığı için değerler tamdır).
  const draft = useWatch({ control: form.control }) as ManualItemFormValues
  const type = settings.types.find((x) => x.name === draft.type)
  const period = draft.period.slice(0, 7)
  const dayNumber = Number(draft.day)
  const suggestion = useQuery({
    queryKey: ['cash', 'suggest', firm, draft.type, period, draft.description],
    queryFn: () => cashApi.suggest(firm, draft.type, `${period}-01`, draft.description),
    enabled: draft.source === 'duzenli' && !!period && !!draft.type && !item,
  })

  const payment =
    draft.source === 'duzenli'
      ? draft.period
        ? previewPaymentDate(
            draft.period,
            draft.day.trim() && Number.isInteger(dayNumber) ? dayNumber : null,
            type,
            start,
          )
        : undefined
      : draft.date || undefined

  return (
    <Sheet open={open} onOpenChange={(o) => !o && editor.requestClose(onClose)}>
      <SheetContent side="right">
        <SheetHeader>
          <SheetTitle>{item ? t.editTitle : t.newTitle}</SheetTitle>
        </SheetHeader>
        <SheetBody>
          {version.alert}
          {version.dialog}
          {editor.dialog}
          <form
            id="cash-manual-form"
            className="flex flex-col gap-3"
            noValidate
            onSubmit={(event) => void editor.save(event)}
          >
            <label className="flex flex-col gap-1 text-xs">
              {t.source}
              <Controller
                control={form.control}
                name="source"
                render={({ field }) => (
                  <OptionSelect
                    value={field.value}
                    options={['duzenli', 'tek'].map((v) => ({ value: v, label: t.sources[v]! }))}
                    onValueChange={(v) => field.onChange(v as ManualItemFormValues['source'])}
                    aria-label={t.source}
                  />
                )}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              {t.type}
              <Controller
                control={form.control}
                name="type"
                render={({ field }) => (
                  <OptionSelect
                    value={field.value}
                    options={settings.types.map((x) => ({
                      value: x.name,
                      label: x.name,
                      group: x.direction,
                    }))}
                    onValueChange={field.onChange}
                    aria-label={t.type}
                  />
                )}
              />
              <FieldError id="cash-manual-type" message={errors.type?.message} />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              {t.descriptionField}
              <Input
                aria-label={t.descriptionField}
                {...fieldErrorProps('cash-manual-description', errors.description?.message)}
                {...form.register('description')}
              />
              <FieldError id="cash-manual-description" message={errors.description?.message} />
            </label>
            {draft.source === 'duzenli' ? (
              <>
                <div className="flex flex-col gap-1 text-xs">
                  <span>{t.period}</span>
                  <Controller
                    control={form.control}
                    name="period"
                    render={({ field }) => (
                      <MonthPicker
                        value={period || undefined}
                        onChange={(p) => field.onChange(`${p}-01`)}
                      />
                    )}
                  />
                  <FieldError id="cash-manual-period" message={errors.period?.message} />
                </div>
                <label className="flex flex-col gap-1 text-xs">
                  {t.day}
                  <Input
                    inputMode="numeric"
                    className="w-24"
                    aria-label={t.day}
                    {...fieldErrorProps('cash-manual-day', errors.day?.message)}
                    {...form.register('day')}
                  />
                  <FieldError id="cash-manual-day" message={errors.day?.message} />
                </label>
              </>
            ) : (
              <label className="flex flex-col gap-1 text-xs">
                {t.date}
                <Controller
                  control={form.control}
                  name="date"
                  render={({ field }) => (
                    <DateInput value={field.value} onChange={field.onChange} aria-label={t.date} />
                  )}
                />
                <FieldError id="cash-manual-date" message={errors.date?.message} />
              </label>
            )}
            {payment !== undefined && (
              <p className="text-sm font-medium" data-testid="cash-manual-payment-date">
                {payment ? t.paymentDateValue(formatDateOnly(payment)) : t.paidBeforeStart}
              </p>
            )}
            <label className="flex flex-col gap-1 text-xs">
              {t.amount}
              <Input
                inputMode="decimal"
                aria-label={t.amount}
                {...fieldErrorProps('cash-manual-amount', errors.amount?.message)}
                {...form.register('amount')}
              />
              <FieldError id="cash-manual-amount" message={errors.amount?.message} />
            </label>
            {suggestion.data?.amount != null && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-text-secondary">
                  {t.suggestion(
                    formatDecimal(suggestion.data.amount),
                    t.basis[suggestion.data.basis ?? ''] ?? '',
                  )}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    form.setValue('amount', String(suggestion.data!.amount), {
                      shouldDirty: true,
                      shouldValidate: form.formState.isSubmitted,
                    })
                  }
                >
                  {t.useSuggestion}
                </Button>
              </div>
            )}
            <label className="flex flex-col gap-1 text-xs">
              {t.instrument}
              <Controller
                control={form.control}
                name="instrument"
                render={({ field }) => (
                  <OptionSelect
                    value={field.value}
                    options={INSTRUMENTS.map((v) => ({ value: v, label: v || 'Banka' }))}
                    onValueChange={field.onChange}
                    aria-label={t.instrument}
                  />
                )}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              {t.cariCode}
              <Input
                aria-label={t.cariCode}
                {...fieldErrorProps('cash-manual-cari', errors.cari_code?.message)}
                {...form.register('cari_code')}
              />
              <FieldError id="cash-manual-cari" message={errors.cari_code?.message} />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              {t.note}
              <Input
                aria-label={t.note}
                {...fieldErrorProps('cash-manual-note', errors.note?.message)}
                {...form.register('note')}
              />
              <FieldError id="cash-manual-note" message={errors.note?.message} />
            </label>
          </form>
          {item?.source === 'duzenli' && (
            <div className="mt-6 flex flex-wrap items-end gap-2 border-t pt-4">
              <label className="flex flex-col gap-1 text-xs">
                {t.copyMonths}
                <Input
                  inputMode="numeric"
                  className="w-20"
                  value={copyMonths}
                  onChange={(e) => setCopyMonths(e.target.value)}
                  aria-label={t.copyMonths}
                />
              </label>
              <label className="flex flex-col gap-1 text-xs">
                {t.copyIncrease}
                <Input
                  inputMode="decimal"
                  className="w-20"
                  value={copyIncrease}
                  onChange={(e) => setCopyIncrease(e.target.value)}
                  aria-label={t.copyIncrease}
                />
              </label>
              <Button
                type="button"
                variant="secondary"
                loading={copy.isPending}
                onClick={() => copy.mutate()}
              >
                <Copy />
                {t.copy}
              </Button>
            </div>
          )}
        </SheetBody>
        <SheetFooter>
          <Button type="submit" form="cash-manual-form" loading={editor.saving}>
            {messages.cash.planning.save}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
